/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 2 hook(s) need dependency review; fix each and remove this directive */
import { getBackendUrl } from "@Utils";
import { useTranslation } from "@i18n";
import { lazy, Suspense, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@Reducers/index";
import { BsThreeDotsVertical, BsExclamationOctagon } from "react-icons/bs";
import { AiOutlineOneToOne } from "react-icons/ai";
import VersionRowMenu, { type MenuNode } from "./VersionRowMenu";
import MarkRevisionModal from "./MarkRevisionModal";
import { fetchRevisionSeries, type RevisionDefaultBehavior } from "../revisionSeriesShared";
import {
  getEffectiveSequenceRules,
  type RevisionSequenceRules,
  type RevisionSequenceType,
} from "@Helpers/revisionPattern";
import { getViewerUrl } from "@Helpers";
import { createFileVersion, refreshFileManager, getFileVersions } from "@Actions/fileUpload";
import { downloadFile } from "@Services/fileDownload";
import { convertDate } from "@Services/luxonHelper";
import { formatBytes, PDF_TYPE, TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import { showToastNotification } from "@Reducers/toastSlice";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import Text from "@CubeComponents/html/Text/Text";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import RadioButton from "@CubeComponents/Mui_Form/RadioButton";
import axiosWrapper from "@AxiosWrapper";
import { getTransmittalFileVersionsForSpace } from "@Features/transmittals";
import { useNavigate } from "react-router-dom";
import { HoopsViewerContext } from "@Features/viewer-core";
import { PdfSessionProvider } from "@Features/pdf-viewer/state/PdfSessionContext";
import UserNameTagComponent from "@CubeComponents/html/UserNameTagComponent";
import DropdownButtonComponent from "@CubeComponents/form/DropdownButtonComponentV1";
import _ from "lodash";
import Box from "@mui/material/Box";
import VersionNotesComponent from "@Components/VersionNotes";
import type { GetExtendedResponse, UpdateResponse } from "@ServerTypes/accounts/accounts.schema";
import type { SaveNoteInFileVersionResponse } from "@ServerTypes/fileOperations/fileOperations.schemas";
import type { GetDsrVersionsResponse, RestoreCostDatabaseVersionResponse } from "@ServerTypes/dsr/dsr.schema";
import { SimpleEditor } from "@CubeComponents/RichTextEditor/components/tiptap-templates/simple/ControlledSimpleEditor";
import { sanitizeHtml } from "@Utils/sanitize";

const PdfViewer = lazy(() => import("@Features/pdf-viewer/components/PdfViewer/PdfViewer"));

interface VersionDataItem {
  id?: string;
  _id?: string;
  fileVersionNumber?: number;
  createdDate?: number;
  updatedDate?: number;
  modifiedDate?: number;
  createdByName?: string;
  resourceType?: string;
  metadata?: { size?: number };
  uploadByData?: { id?: string; fullName?: string };
  upgradeReason?: { fileName?: string; fileVersionNumber?: number };
  note?: string | null;
  transmittalIndex?: number;
  transmittalVersionId?: string;
  transmittalNo?: number;
  fileVersionId?: string;
  projectId?: string;
  projectCustomId?: string;
  indexAtProjectLevel?: number;
  consumedViaTransmittal?: { indexAtProjectLevel?: number };
  containsIssue?: boolean;
  fileHash?: string;
  createdBy?: string | { _id?: string };
  text?: string;
  publishByData?: { id?: string; date?: number };
  versionNumber?: number | string;
  clashVersionCreator?: { _id?: string };
  revisions?: unknown[];
  revisionSeries?: { seriesId?: string; label?: string; markedBy?: string; markedAt?: number };
}

interface FilesDataProp {
  resourceType?: string;
  hierarchyId?: string;
  id?: string;
  name?: string;
  isContainer?: boolean;
  isEmptyContainer?: boolean;
  dateModified?: number | string;
  userName?: string;
  userId?: string;
  transmittalVersionId?: string;
  createdDate?: number;
  permission?: any;
}

interface CostDatabaseDetailsProp {
  _id?: string;
}

function VersionContent({
  selectedSpaceTypeInFileManager,
  enableFileManagerContent,
  enableCostdatabaseContent,
  enableClashContent,
  clashVersionsDetails,
  costDatabaseDetails,
  filesData = {},
  spaceId,
  originalResourceType,
  handleFileUploadButtonClick,
}: {
  readonly selectedSpaceTypeInFileManager: string;
  readonly enableFileManagerContent: boolean;
  readonly enableCostdatabaseContent: boolean;
  readonly enableClashContent: boolean;
  readonly clashVersionsDetails: VersionDataItem[];
  readonly costDatabaseDetails: CostDatabaseDetailsProp | null | undefined;
  readonly filesData: FilesDataProp | undefined;
  readonly spaceId: string;
  readonly originalResourceType: string;
  readonly handleFileUploadButtonClick: () => void;
}) {
  const { t } = useTranslation();
  const accessPermission = filesData?.permission;
  const [versionDisplayMode, setVersionDisplayMode] = useState<string>("transmittalID");
  const {
    filesSharedUnderResources,
    transmittalVersions,
    getProjectSettingObject,
    projectData,
    refreshFileManager: shouldRefresh,
  } = useSelector((state: RootState) => state);
  const importOnlyUploads = useSelector((state: RootState) => !!state.layout.FILE_MANAGER?.IMPORT_ONLY_UPLOADS);
  const fileVersions = useSelector((state: RootState) => {
    if (filesData) {
      if (
        [
          "RESOURCE_TYPE_FILE",
          "RESOURCE_TYPE_MERGED_FILE",
          "RESOURCE_TYPE_SHORTCUT_FILE",
          "RESOURCE_TYPE_EMBED",
        ].includes(filesData.resourceType ?? "")
      ) {
        return state.fileVersions[filesData.hierarchyId ?? ""] || [];
      }
      return [];
    }
    return [];
  });
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [fileData, setFileData] = useState<VersionDataItem[]>([]);
  const [fileId, setFileId] = useState<string>("");
  const [isRenderPdfViewer, setIsRenderPdfViewer] = useState<boolean>(false);
  const [fileType, setFileType] = useState<string>("");
  const [selectedFilesData, setSelectedFilesData] = useState<VersionDataItem | null>(null);
  const [addNoteModalVisible, setAddNoteModalVisible] = useState(false);
  const [selectedVersionData, setSelectedVersionData] = useState<{
    index: number;
    fileVersionId: string;
    dsrId?: string;
    clashVersionId?: string;
  } | null>(null);
  const [editingNote, setEditingNote] = useState<{ note: string } | null>(null);
  const [revisionSeriesInfo, setRevisionSeriesInfo] = useState<{
    defaultBehavior: RevisionDefaultBehavior | null;
    startPattern: string | null;
    sequenceType: RevisionSequenceType | null;
    sequenceRules: RevisionSequenceRules | null;
  } | null>(null);
  const [removeRevisionTarget, setRemoveRevisionTarget] = useState<VersionDataItem | null>(null);
  const [removeRevisionScope, setRemoveRevisionScope] = useState<"version" | "label">("version");
  const [markRevisionTarget, setMarkRevisionTarget] = useState<VersionDataItem | null>(null);
  const [markRevisionContext, setMarkRevisionContext] = useState<{
    previousLabel: string | null;
    existingLabels: string[];
  }>({ previousLabel: null, existingLabels: [] });
  const [isLoading, setIsLoading] = useState(false);

  const revisionBehavior = revisionSeriesInfo?.defaultBehavior ?? null;
  const effectiveSequenceRules = getEffectiveSequenceRules({
    defaultBehavior: revisionSeriesInfo?.defaultBehavior ?? undefined,
    sequenceRules: revisionSeriesInfo?.sequenceRules ?? undefined,
  });
  const shouldPromptForRevision = (hasPreviousRevision: boolean) => {
    if (effectiveSequenceRules.allowAnyOrder) return true;
    return hasPreviousRevision ? effectiveSequenceRules.allowSkipIntermediate : effectiveSequenceRules.allowSkipFirst;
  };

  const fetchUserSettings = useCallback(async () => {
    const response = await axiosWrapper.post<GetExtendedResponse>(`${getBackendUrl()}/accounts/get`, {
      projectId: projectData.id,
      projection: ["TRANSMITTAL_FILE_VERSION_DROPDOWN"],
    });
    if (response.data.status && response.data.data) {
      const userSettingsForDropdown = _.get(response, "data.data.TRANSMITTAL_FILE_VERSION_DROPDOWN");
      setVersionDisplayMode(userSettingsForDropdown);
    }
  }, [projectData?.id]);

  const updateUserSettings = async (value: string) => {
    if (value === versionDisplayMode) {
      return;
    }
    const response = await axiosWrapper.post<UpdateResponse>(`${getBackendUrl()}/accounts/update`, {
      projectId: projectData.id,
      TRANSMITTAL_FILE_VERSION_DROPDOWN: value,
    });
    if (response.data.status) {
      dispatch(
        showToastNotification({
          content: t("Settings updated successfully."),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
      setVersionDisplayMode(value);
    } else {
      dispatch(
        showToastNotification({
          content: t("Error while updating settings."),
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
  };

  const getVersionDisplayContent = () => {
    if (versionDisplayMode === "transmittalID") {
      return t("Transmittal ID");
    } else if (versionDisplayMode === "fileVersion") {
      return t("File Version");
    } else {
      return t("File Version in Origin Space");
    }
  };

  const getVersionDisplayDropdownItems = () =>
    _.get(getProjectSettingObject?.transmittal, "viewOriginVersion.enable", false)
      ? [
          { text: t("Transmittal ID"), id: "transmittalID" },
          { text: t("File Version"), id: "fileVersion" },
          { text: t("File Version in Origin Space"), id: "fileVersionOriginSpace" },
        ]
      : [
          { text: t("Transmittal ID"), id: "transmittalID" },
          { text: t("File Version"), id: "fileVersion" },
        ];

  useEffect(() => {
    if (filesSharedUnderResources && projectData?.id) {
      fetchUserSettings();
    }
  }, [projectData?.id, filesSharedUnderResources, fetchUserSettings]);

  useEffect(() => {
    if (!enableFileManagerContent || selectedSpaceTypeInFileManager !== "this") {
      setRevisionSeriesInfo(null);
      return;
    }
    const fileIdForSeries = filesData?.hierarchyId;
    if (
      !fileIdForSeries ||
      !["RESOURCE_TYPE_FILE", "RESOURCE_TYPE_MERGED_FILE", "RESOURCE_TYPE_SHORTCUT_FILE"].includes(
        filesData?.resourceType ?? "",
      )
    ) {
      setRevisionSeriesInfo(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { defaultBehavior, startPattern, sequenceType, sequenceRules } =
          await fetchRevisionSeries(fileIdForSeries);
        if (!cancelled) setRevisionSeriesInfo({ defaultBehavior, startPattern, sequenceType, sequenceRules });
      } catch {
        if (!cancelled) setRevisionSeriesInfo(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    filesData?.hierarchyId,
    filesData?.resourceType,
    enableFileManagerContent,
    selectedSpaceTypeInFileManager,
    shouldRefresh,
  ]);

  useEffect(() => {
    if (enableFileManagerContent) {
      const fileParams = filesData;
      let fileId = "";

      if (
        ["RESOURCE_TYPE_FILE", "RESOURCE_TYPE_MERGED_FILE", "RESOURCE_TYPE_SHORTCUT_FILE"].includes(
          fileParams.resourceType ?? "",
        )
      ) {
        fileId = filesData.hierarchyId ?? "";
      }
      setFileId(fileId);
      initialiseFileData();

      if (filesData.resourceType == "RESOURCE_TYPE_ESTIMATE") {
        const rawData = localStorage.getItem("estimateSnapshot");
        if (rawData) {
          const data = JSON.parse(rawData) as Record<string, number>;
          const array: VersionDataItem[] = [];
          for (let i = 1; i <= (data[filesData.id!] ?? 0); i++) {
            const selectedData = filesData;
            array.push({
              modifiedDate: selectedData.createdDate,
              createdByName: selectedData.userName,
            });
          }
          setFileData(array);
        }
      }
    } else if (enableCostdatabaseContent && costDatabaseDetails?._id) {
      getDsrVersions();
    } else if (enableClashContent) {
      setFileData(clashVersionsDetails);
    }
  }, [costDatabaseDetails?._id]);

  async function getDsrVersions() {
    await axiosWrapper
      .post<GetDsrVersionsResponse>(`${getBackendUrl()}/dsr/getDsrVersions`, {
        dsrId: costDatabaseDetails!._id,
        unNamedVersion: true,
      })
      .then((res) => {
        setFileData((res.data.data ?? []) as VersionDataItem[]);
      });
  }

  const getAvatarWithTag = ({ id }: { id?: string }) => <UserNameTagComponent id={id ?? ""} />;

  const initialiseFileData = () => {
    if (selectedSpaceTypeInFileManager == "this") {
      const sortedFileVersions: VersionDataItem[] = Array.isArray(fileVersions)
        ? [...fileVersions].sort((a, b) => (b.modifiedDate ?? 0) - (a.modifiedDate ?? 0))
        : [];
      if (filesData.isContainer && !sortedFileVersions.find((x) => x.fileVersionNumber == 0)) {
        sortedFileVersions.push({
          fileVersionNumber: 0,
          metadata: {
            size: 0,
          },
          createdDate: Number(filesData.dateModified),
          uploadByData: {
            fullName: filesData.userName,
            id: filesData.userId,
          },
          revisions: [],
        });
      }
      setFileData(sortedFileVersions);
    } else if (filesSharedUnderResources) {
      dispatch(
        getTransmittalFileVersionsForSpace({
          fileId: filesData.hierarchyId ?? "",
          spaceId,
        }),
      );
      setFileDataForExternal();
    }
  };

  useEffect(() => {
    if (enableFileManagerContent) {
      initialiseFileData();
    }
  }, [
    fileVersions,
    filesData,
    selectedSpaceTypeInFileManager,
    fileId,
    filesSharedUnderResources,
    enableFileManagerContent,
  ]);

  useEffect(() => {
    if (enableFileManagerContent && shouldRefresh) {
      dispatch(
        getFileVersions({
          resourceType: filesData.resourceType ?? "",
          fileId: filesData.hierarchyId ?? "",
        }),
      );
      dispatch(refreshFileManager(false));
    }
  }, [shouldRefresh, enableFileManagerContent, filesData.hierarchyId, filesData.resourceType]);

  const setFileDataForExternal = async () => {
    let sharingData: VersionDataItem[] = [];
    transmittalVersions?.forEach((val) => {
      sharingData.push({
        createdDate: val.createdDate,
        createdBy: val.createdBy as VersionDataItem["createdBy"],
        metadata: {
          size: val.matchingVersion?.size,
        },
        fileVersionNumber: val.matchingVersion?.fileVersionNumber,
        transmittalNo: val.indexAtProjectLevel,
        id: val.matchingVersion?._id,
      });
    });
    sharingData = sharingData.sort((a, b) => (b.createdDate ?? 0) - (a.createdDate ?? 0));
    setFileData(sharingData);
  };

  const getPopOverItems = (index: number): MenuNode[] => {
    const items: MenuNode[] = [];
    if (enableFileManagerContent) {
      if (index != 0) {
        if (selectedSpaceTypeInFileManager == "this" && fileData[index]?.resourceType != "RESOURCE_TYPE_MERGED_FILE") {
          items.push({ text: t("Restore as top version"), id: "restore" });
        }
        items.push({ text: t("Preview"), id: "preview" });
      }
      if (!filesData.isEmptyContainer && filesData.resourceType !== "RESOURCE_TYPE_EMBED") {
        items.push({ text: t("Download"), id: "download" });
      }
      if (selectedSpaceTypeInFileManager == "this") {
        if (fileData[index]?.note) {
          items.push({ text: t("Edit Note"), id: "note" }, { text: t("Delete Note"), id: "deleteNote" });
        } else {
          items.push({ text: t("Add Note"), id: "note" });
        }

        if (fileData[index]?.fileVersionNumber) {
          const isMarked = !!fileData[index]?.revisionSeries?.label;
          const hasPreviousRevision = fileData.slice(index + 1).some((v: any) => v?.revisionSeries?.label);

          if (isMarked) {
            items.push({ text: t("Remove Revision"), id: "removeRevision" });
          } else if (revisionBehavior === "ALWAYS_NEW") {
            items.push({ text: t("Mark as Revision"), id: "markNewRev" });
          } else if (revisionBehavior === "ALWAYS_PREVIOUS") {
            items.push({
              text: t("Mark as Revision"),
              id: hasPreviousRevision ? "markSamePrev" : "markNewRev",
            });
          } else {
            if (hasPreviousRevision) {
              items.push({
                id: "markRevisionParent",
                text: t("Mark as"),
                children: effectiveSequenceRules.noRepeatedRevisions
                  ? [{ text: t("New Revision"), id: "markNewRev" }]
                  : [
                      { text: t("Same Revision as Previous"), id: "markSamePrev" },
                      { text: t("New Revision"), id: "markNewRev" },
                    ],
              });
            } else {
              items.push({ text: t("Mark as Revision"), id: "markNewRev" });
            }
          }
        }
      }
    } else if (enableCostdatabaseContent) {
      items.push({ text: t("Preview"), id: "preview" });
      if (index != 0) {
        items.push({ text: t("Save as top version"), id: "restore" });
      }
      items.push({ text: fileData[index]?.note ? t("Edit Note") : t("Add Note"), id: "note" });
    } else if (enableClashContent) {
      items.push({ text: fileData[index]?.note ? t("Edit Note") : t("Add Note"), id: "note" });
    }
    return items;
  };

  const previewFile = (data: VersionDataItem) => {
    if (enableFileManagerContent) {
      let location = getViewerUrl({
        fileVersionId: data.id ?? "",
        spaceId,
        projectId: data.projectId ?? "",
      });

      if (data.transmittalVersionId) {
        location = getViewerUrl({
          fileVersionId: data.fileVersionId ?? "",
          spaceId,
          transmittalVersionId: data.transmittalVersionId,
          projectId: data.projectId ?? "",
        });
      }
      if (location) {
        navigate(location);
      }
    } else if (enableCostdatabaseContent) {
      window.open(`${window.location.href}/${costDatabaseDetails?._id}/dsrVersion/${data.id}`, "_blank");
    }
  };

  const restoreFile = async (id: string) => {
    if (enableFileManagerContent && !accessPermission?.write) {
      dispatch(
        showToastNotification({
          content: t("Insufficient permission to perform this action."),
          type: TOAST_TYPE_ERROR,
        }),
      );
      return;
    }
    if (enableFileManagerContent) {
      dispatch(
        createFileVersion({
          fileVersionId: id,
          spaceId,
        }),
      );
    } else if (enableCostdatabaseContent) {
      await axiosWrapper
        .post<RestoreCostDatabaseVersionResponse>(`${getBackendUrl()}/dsr/restoreCostDatabaseVersion`, {
          id,
          dsrId: costDatabaseDetails!._id,
        })
        .then((res) => {
          if (res.data.status) {
            getDsrVersions();
          }
        });
    }
  };

  const renderPdf = (fileExtension: string, data: VersionDataItem) => {
    if (fileExtension === PDF_TYPE[0]) {
      setIsRenderPdfViewer(true);
      setFileType(fileExtension);
      setSelectedFilesData(data);
    }
  };

  const handleFileDownLoad = (data: VersionDataItem) => {
    const fileExtension = filesData.name?.split(".").pop() ?? "";
    const fileVersonId = data.id ?? "";
    if (selectedSpaceTypeInFileManager == "external" && data.transmittalNo) {
      const downloadData = {
        fileVersionIds: [fileVersonId],
        folderIds: [],
        type: "Download",
        transmittalNo: data.transmittalNo,
        fileName: filesData.name,
      };
      renderPdf(fileExtension, data);
      downloadFile(downloadData);
    } else {
      renderPdf(fileExtension, data);
      downloadFile({
        fileVersionIds: [fileVersonId],
        folderIds: [],
        type: "Download",
      });
    }
  };

  const handleSelectedOption = (id: string, data: VersionDataItem, index: number) => {
    if (id == "download") {
      if (accessPermission.download) {
        handleFileDownLoad(data);
      } else {
        dispatch(
          showToastNotification({
            content: t("Insufficient permission to perform this action."),
            type: TOAST_TYPE_ERROR,
          }),
        );
      }
    } else if (id == "preview") {
      previewFile(data);
    } else if (id == "restore") {
      restoreFile(data.id ?? "");
    } else if (id == "note") {
      openNoteModal(index);
    } else if (id == "deleteNote") {
      handleDeleteNote(index);
    } else if (id == "markNewRev") {
      const hasPreviousRevision = fileData.slice(index + 1).some((v) => v?.revisionSeries?.label);
      if (shouldPromptForRevision(hasPreviousRevision)) {
        openMarkRevisionModal(data, index);
      } else {
        handleMarkVersion(data, "new");
      }
    } else if (id == "markSamePrev") {
      handleMarkVersion(data, "same-as-previous");
    } else if (id == "removeRevision") {
      openRemoveRevisionModal(data);
    }
  };

  const callRevisionApi = async (
    endpoint: string,
    body: Record<string, any>,
    successMsg: string,
    errorMsg: string,
  ): Promise<{ status?: boolean; label?: string; errorMessage?: string } | null> => {
    if (isLoading) return null;
    setIsLoading(true);
    try {
      const response = await axiosWrapper.post<{ status?: boolean; label?: string; errorMessage?: string }>(
        `${getBackendUrl()}/fileUpload/${endpoint}`,
        body,
      );
      if (response.data?.status) {
        dispatch(showToastNotification({ content: successMsg as any, type: TOAST_TYPE_SUCCESS }));
      } else {
        dispatch(
          showToastNotification({
            content: (response.data?.errorMessage || errorMsg) as any,
            type: TOAST_TYPE_ERROR,
          }),
        );
      }
      return response.data ?? null;
    } catch {
      dispatch(showToastNotification({ content: errorMsg as any, type: TOAST_TYPE_ERROR }));
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const handleMarkVersion = async (data: VersionDataItem, mode: "new" | "same-as-previous") => {
    if (!data.id) return;
    const result = await callRevisionApi(
      "markVersionAsRevision",
      { fileVersionId: data.id, mode },
      t("Revision marked"),
      t("Could not mark revision"),
    );
    if (result?.status) {
      dispatch(
        getFileVersions({
          resourceType: filesData.resourceType ?? "",
          fileId: filesData.hierarchyId ?? "",
        }),
      );
    }
  };

  const openMarkRevisionModal = (data: VersionDataItem, index: number) => {
    if (!data.id) return;
    const previousLabel =
      fileData.slice(index + 1).find((v) => v?.revisionSeries?.label)?.revisionSeries?.label ?? null;
    const existingLabels = fileData
      .filter((v) => v?.revisionSeries?.label && v.id !== data.id)
      .map((v) => v.revisionSeries!.label!);
    setMarkRevisionContext({ previousLabel, existingLabels });
    setMarkRevisionTarget(data);
  };

  const closeMarkRevisionModal = () => setMarkRevisionTarget(null);

  const confirmMarkRevision = async (label: string): Promise<string | null> => {
    if (!markRevisionTarget?.id || isLoading) return null;
    setIsLoading(true);
    try {
      const response = await axiosWrapper.post<{ status?: boolean; errorMessage?: string }>(
        `${getBackendUrl()}/fileUpload/markVersionAsRevision`,
        { fileVersionId: markRevisionTarget.id, mode: "new", label },
      );
      if (response.data?.status) {
        dispatch(showToastNotification({ content: t("Revision marked") as any, type: TOAST_TYPE_SUCCESS }));
        dispatch(
          getFileVersions({
            resourceType: filesData.resourceType ?? "",
            fileId: filesData.hierarchyId ?? "",
          }),
        );
        closeMarkRevisionModal();
        return null;
      }
      return response.data?.errorMessage || t("Could not mark revision");
    } catch {
      return t("Could not mark revision");
    } finally {
      setIsLoading(false);
    }
  };

  const openRemoveRevisionModal = (data: VersionDataItem) => {
    if (!data.id) return;
    setRemoveRevisionScope("version");
    setRemoveRevisionTarget(data);
  };

  const closeRemoveRevisionModal = () => setRemoveRevisionTarget(null);

  const confirmRemoveRevision = async () => {
    if (!removeRevisionTarget?.id) return;
    const versionId = removeRevisionTarget.id;
    const scope = removeRevisionScope;
    const result = await callRevisionApi(
      "removeVersionRevision",
      { fileVersionId: versionId, scope },
      t("Revision removed"),
      t("Could not remove revision"),
    );
    if (result?.status) {
      dispatch(
        getFileVersions({
          resourceType: filesData.resourceType ?? "",
          fileId: filesData.hierarchyId ?? "",
        }),
      );
    }
    closeRemoveRevisionModal();
  };

  const versionDisplayDropdownHandler = (args: string | number) => {
    updateUserSettings(String(args));
  };

  const openNoteModal = (index: number) => {
    const data = fileData[index];
    const currentNote = data?.note || "";

    setSelectedVersionData({
      index,
      fileVersionId: data?.id ?? "",
      dsrId: enableCostdatabaseContent ? costDatabaseDetails?._id : undefined,
      clashVersionId: enableClashContent ? data?._id : undefined,
    });

    if (currentNote) {
      setEditingNote({ note: currentNote });
    } else {
      setEditingNote(null);
    }

    setAddNoteModalVisible(true);
  };

  const handleCloseModal = () => {
    setAddNoteModalVisible(false);
    setSelectedVersionData(null);
    setEditingNote(null);
  };

  const handleDeleteNote = async (index: number) => {
    const data = fileData[index];
    const noteData = {
      action: "delete",
      fileVersionId: data?.id ?? "",
    };
    const response = await axiosWrapper.post<SaveNoteInFileVersionResponse>(
      `${getBackendUrl()}/fileOperations/saveNoteInFileVersion`,
      noteData,
    );

    if (response.data.status) {
      const updatedData = [...fileData];
      updatedData[index] = { ...updatedData[index], note: null };
      setFileData(updatedData);

      dispatch(
        showToastNotification({
          content: t("Note deleted successfully"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    } else {
      dispatch(
        showToastNotification({
          content: t("Note could not be deleted"),
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
  };

  const getUploadButton = () => {
    if (importOnlyUploads) {
      return null;
    }
    if (filesData.resourceType == "RESOURCE_TYPE_ESTIMATE") {
      return null;
    }
    if (
      originalResourceType == "RESOURCE_TYPE_SHORTCUT_MERGED_FILE" ||
      originalResourceType == "RESOURCE_TYPE_SHORTCUT_FILE" ||
      filesData.transmittalVersionId
    ) {
      return null;
    }
    return (
      <Box>
        {selectedSpaceTypeInFileManager == "this" && ["RESOURCE_TYPE_FILE"].includes(filesData.resourceType ?? "") && (
          <Box className="mb-5">
            <ButtonComponent buttonType={BUTTON_TYPES.GRADIENT_BUTTON} onClick={handleFileUploadButtonClick}>
              <Text>{t("Upload New")}</Text>
            </ButtonComponent>
          </Box>
        )}
      </Box>
    );
  };

  function handleCloseIsRenderPdfViewer() {
    setIsRenderPdfViewer(false);
    setFileType("");
  }

  const getVersionNumber = (val: VersionDataItem, index: number, totalLength: number) => {
    if (selectedSpaceTypeInFileManager === "external") {
      if (versionDisplayMode === "transmittalID") {
        return `TS-${val.transmittalNo}`;
      } else if (versionDisplayMode === "fileVersion") {
        return `V${totalLength - index}`;
      } else if (versionDisplayMode === "fileVersionOriginSpace") {
        return `V${val.fileVersionNumber}`;
      }
    } else if (selectedSpaceTypeInFileManager === "this") {
      if (versionDisplayMode === "transmittalID") {
        return `TS-${val.transmittalIndex}`;
      } else if (versionDisplayMode === "fileVersion") {
        return `V${totalLength - index}`;
      } else if (versionDisplayMode === "fileVersionOriginSpace") {
        return `V${val.fileVersionNumber}`;
      }
    }
    return null;
  };

  let moduleType: "clash" | "file" | "costDatabase" | "schedule" | "estimate" | "dsr" = "clash";

  if (enableFileManagerContent) {
    moduleType = "file";
  } else if (enableCostdatabaseContent) {
    moduleType = "costDatabase";
  }

  return (
    <Box className="text-xs">
      {enableFileManagerContent && getUploadButton()}
      <Box>
        {isRenderPdfViewer === true ? (
          <Box className="pdfViewer" style={{ display: "none" }}>
            <PdfSessionProvider isPrivate>
              <HoopsViewerContext.Provider
                value={
                  {
                    fileVersionId: selectedFilesData?.id || "",
                    fileVersionData: {
                      ...selectedFilesData,
                      EDN: `${selectedFilesData?.projectCustomId || ""}-${selectedFilesData?.indexAtProjectLevel || ""}`,
                    },
                    fileType: {
                      fileType,
                    },
                  } as any
                }
              >
                <Suspense fallback={<></>}>
                  <PdfViewer
                    {...({
                      selectedFilesData: {
                        ...selectedFilesData,
                        EDN: `${selectedFilesData?.projectCustomId || ""}-${selectedFilesData?.indexAtProjectLevel || ""}`,
                      },
                      fileType: fileType,
                      triggerFileDownload: true,
                      handleCloseIsRenderPdfViewer: handleCloseIsRenderPdfViewer,
                    } as any)}
                  />
                </Suspense>
              </HoopsViewerContext.Provider>
            </PdfSessionProvider>
          </Box>
        ) : null}

        {(["RESOURCE_TYPE_SHORTCUT_FILE"].includes(filesData.resourceType ?? "") ||
          selectedSpaceTypeInFileManager === "external") && (
          <Box className="flex p-2 gap-2 items-center">
            <Box className="text-sm font-semibold text-primary_text">
              <Text>{t("Displayed as")}</Text>
            </Box>
            <DropdownButtonComponent
              content={getVersionDisplayContent()}
              buttonType={BUTTON_TYPES.DROPDOWN_BACKGROUND_BUTTON}
              items={getVersionDisplayDropdownItems()}
              select={versionDisplayDropdownHandler}
            />
          </Box>
        )}

        {fileData?.map((val: VersionDataItem, index: number) => {
          let dateToConvert;
          if (enableFileManagerContent) {
            dateToConvert = val.createdDate;
          } else if (enableClashContent) {
            dateToConvert = val.createdDate;
          } else {
            dateToConvert = val.publishByData?.date;
          }
          const formattedDate = convertDate(Number(dateToConvert), "dateForText");
          return (
            <Box
              key={`fileVersionRow_${index}`}
              className={`flex mb-5 min-w-0 gap-2 overflow-hidden ${enableCostdatabaseContent && "ps-3"}`}
            >
              <Box
                className={`threeDotForPopover ${val.fileVersionNumber || enableCostdatabaseContent ? "" : "hidden"}`}
              >
                <VersionRowMenu
                  trigger={<BsThreeDotsVertical />}
                  items={getPopOverItems(index)}
                  onSelect={(id) => handleSelectedOption(id, val, index)}
                />
              </Box>
              <Box
                className={`font-bold ${enableCostdatabaseContent ? "" : val.fileVersionNumber ? "ms-5" : "ms-8"} w-12`}
              >
                {enableFileManagerContent &&
                  filesData.resourceType == "RESOURCE_TYPE_ESTIMATE" &&
                  `V${fileData.length - index}`}
                {enableFileManagerContent &&
                  selectedSpaceTypeInFileManager == "this" &&
                  filesData.resourceType != "RESOURCE_TYPE_SHORTCUT_FILE" &&
                  `V${val.fileVersionNumber}`}
                {enableFileManagerContent &&
                  selectedSpaceTypeInFileManager == "this" &&
                  filesData.resourceType == "RESOURCE_TYPE_SHORTCUT_FILE" &&
                  getVersionNumber(val, index, fileData.length)}
                {enableCostdatabaseContent && val.text}
                {enableFileManagerContent && selectedSpaceTypeInFileManager == "external" && (
                  <Box>{getVersionNumber(val, index, fileData.length)}</Box>
                )}
                {enableClashContent && val.versionNumber}
              </Box>
              <Box className="w-full min-w-0 overflow-hidden ms-3">
                <Box className="flex flex-col gap-1 min-w-0 overflow-hidden">
                  <Box className="flex justify-between h-[1.375rem]">
                    <Box className="flex items-center gap-2">
                      <span>{formattedDate}</span>
                      {val.revisionSeries?.label && (
                        <span className="rounded-full bg-[#666a84] px-3 py-1 text-xs font-medium leading-none text-white">
                          {val.revisionSeries.label}
                        </span>
                      )}
                    </Box>
                    {enableFileManagerContent ||
                      (enableClashContent && index == 0 && (
                        <span className="ms-2 h-4 border border-[#45cf80] bg-[#d8f2ff] px-2.5 text-[0.625rem] text-[#45cf80]">
                          <Text>{t("Top Version")}</Text>
                        </span>
                      ))}
                    {enableFileManagerContent &&
                    filesData.resourceType != "RESOURCE_TYPE_ESTIMATE" &&
                    filesData.resourceType != "RESOURCE_TYPE_MERGED_FILE" ? (
                      <Box>{formatBytes(val.metadata?.size ?? 0)}</Box>
                    ) : null}
                  </Box>
                  <Box>
                    {enableFileManagerContent &&
                      filesData.resourceType == "RESOURCE_TYPE_ESTIMATE" &&
                      `Created by ${val.createdByName}`}
                    {enableFileManagerContent && selectedSpaceTypeInFileManager == "this" && !val.upgradeReason && (
                      <Box className="flex justify-between">
                        <Box className="flex flex-wrap gap-1 items-center">
                          {val.consumedViaTransmittal ? (
                            <>
                              <span className="whitespace-normal">
                                <Text>{t("Consumed by")}</Text>
                              </span>
                              {getAvatarWithTag({
                                id: val?.uploadByData?.id,
                              })}
                              <span className="whitespace-normal">
                                <Text>
                                  {t("consumed_via_transmittal", {
                                    transmittalNo: val?.consumedViaTransmittal?.indexAtProjectLevel,
                                  })}
                                </Text>
                              </span>
                            </>
                          ) : (
                            <>
                              <span className="whitespace-normal">
                                <Text>{t(val.fileVersionNumber === 1 ? "Created By" : "Uploaded by")}</Text>{" "}
                              </span>
                              {getAvatarWithTag({
                                id: val?.uploadByData?.id,
                              })}
                            </>
                          )}
                        </Box>
                        {val.containsIssue ||
                          (!!val.fileHash && val.fileHash === fileData[index + 1]?.fileHash && (
                            <Box className="inline-flex items-center justify-end gap-1.5 rounded bg-[#ecf3fe] p-1.5">
                              {val.containsIssue ? (
                                <ToolTipComponent
                                  showTipPointer={false}
                                  position="bottom"
                                  content={t("Contains issues")}
                                >
                                  <BsExclamationOctagon size={14} />
                                </ToolTipComponent>
                              ) : (
                                <ToolTipComponent
                                  showTipPointer={false}
                                  position="bottom"
                                  content={t("Same as previous version")}
                                >
                                  <AiOutlineOneToOne size={16} />
                                </ToolTipComponent>
                              )}
                            </Box>
                          ))}
                      </Box>
                    )}
                    {enableFileManagerContent &&
                      selectedSpaceTypeInFileManager == "this" &&
                      val.upgradeReason &&
                      `Automatic due to version update of ${val.upgradeReason?.fileName} to V${val.upgradeReason?.fileVersionNumber}`}
                    {enableFileManagerContent && selectedSpaceTypeInFileManager == "external" && (
                      <Box className="flex gap-1 items-center">
                        <span>
                          <Text>{t("Shared by")}</Text>
                        </span>
                        {getAvatarWithTag({
                          id: typeof val.createdBy === "object" ? val.createdBy?._id : undefined,
                        })}
                      </Box>
                    )}
                    {enableCostdatabaseContent && (
                      <Box className="flex gap-1 items-center">
                        <span>
                          <Text>{t("Published by")}</Text>{" "}
                        </span>
                        {getAvatarWithTag({
                          id: val.publishByData?.id,
                        })}
                      </Box>
                    )}
                    {enableClashContent && (
                      <Box className="flex gap-1">
                        <span>
                          <Text>{t("Uploaded by")}</Text>{" "}
                        </span>
                        {getAvatarWithTag({
                          id: val.clashVersionCreator?._id,
                        })}
                      </Box>
                    )}
                  </Box>
                </Box>
                {(selectedSpaceTypeInFileManager == "this" || enableCostdatabaseContent || enableClashContent) &&
                val.note ? (
                  <Box className="flex flex-col items-start border border-surface_stroke_gradient_color1 bg-surface_neutral gap-6 p-4 mt-3">
                    <SimpleEditor value={sanitizeHtml(val.note)} editable={false} showToolbar={false} />
                  </Box>
                ) : null}
              </Box>
            </Box>
          );
        })}
      </Box>
      <VersionNotesComponent
        isVisible={addNoteModalVisible}
        onClose={handleCloseModal}
        module={moduleType}
        moduleData={{
          fileVersionId: selectedVersionData?.fileVersionId,
          dsrId: selectedVersionData?.dsrId,
          id: selectedVersionData?.fileVersionId,
          clashVersionId: selectedVersionData?.clashVersionId,
        }}
        existingNote={editingNote?.note || ""}
        onSuccess={(savedNote: string) => {
          if (selectedVersionData?.index !== null) {
            const updatedData = [...fileData];
            updatedData[selectedVersionData!.index] = {
              ...updatedData[selectedVersionData!.index],
              note: savedNote,
            };
            setFileData(updatedData);
          }
          if (enableCostdatabaseContent) {
            getDsrVersions();
          }
        }}
      />
      {removeRevisionTarget && (
        <ModalStateDialogComponent
          isVisible
          width="31.25rem"
          headerTitle={<p className="text-2xl font-bold">{t("Remove Revision")}</p>}
          onCancel={closeRemoveRevisionModal}
          modelContent={
            <div className="flex flex-col gap-4 py-2">
              <span>{t("Are you sure you want to remove Revision?")}</span>
              <div className="flex flex-col gap-2">
                <RadioButton
                  name="removeRevisionScope-version"
                  checked={removeRevisionScope === "version"}
                  onChange={() => setRemoveRevisionScope("version")}
                  label={t("Remove from this version only")}
                />
                <RadioButton
                  name="removeRevisionScope-label"
                  checked={removeRevisionScope === "label"}
                  onChange={() => setRemoveRevisionScope("label")}
                  label={t("Remove from all versions")}
                />
              </div>
              {removeRevisionScope === "label" && (
                <div className="rounded-lg p-3 text-sm bg-banner_color_yellow_translucent">
                  {t(
                    "This revision label will be removed from all versions sharing it. Remaining revisions will be renumbered. This action cannot be undone.",
                  )}
                </div>
              )}
            </div>
          }
          footerContent={
            <div className="flex gap-4">
              <ButtonComponent
                buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
                onClick={confirmRemoveRevision}
                loading={isLoading}
              >
                {t("Confirm")}
              </ButtonComponent>
              <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={closeRemoveRevisionModal}>
                {t("Cancel")}
              </ButtonComponent>
            </div>
          }
        />
      )}
      {markRevisionTarget && (
        <MarkRevisionModal
          versionNumber={markRevisionTarget.fileVersionNumber}
          startPattern={revisionSeriesInfo?.startPattern ?? null}
          sequenceType={revisionSeriesInfo?.sequenceType ?? null}
          previousLabel={markRevisionContext.previousLabel}
          existingLabels={markRevisionContext.existingLabels}
          rules={effectiveSequenceRules}
          isSubmitting={isLoading}
          onConfirm={confirmMarkRevision}
          onClose={closeMarkRevisionModal}
        />
      )}
    </Box>
  );
}

export default VersionContent;
